import React, { useState } from "react";
import { toast } from "react-hot-toast";
import Modal from "../modal";
import Button from "../new-button";
import TextInput from "../form/text-input";
import style from "./style.module.scss";
import { US_DIAL, US_PHONE_EXAMPLE, nationalDigits } from "../../../constants";
import {
  sendPhoneVerificationCode,
  verifyPhoneVerificationCode,
  Logout,
} from "../../../api";

// The API answers 422 with {success:false, message}, which axios surfaces as a
// rejection carrying the same body.
const messageOf = (error, fallback) => error?.data?.message || fallback;

/**
 * Post-login prompt asking the user to verify the cell number on their account.
 * `mandatory` is the wall: routes.js renders it instead of the app, since
 * `phone.verified` is refusing everything behind it — no close button, no backdrop
 * dismiss, and signing out is the only other way off it.
 *
 * `prefill` (national digits) starts the form on a number the user typed elsewhere —
 * the personal details form routes its edit through here, since the number cannot
 * change without a code either way.
 */
const PhoneVerificationModal = ({ isOpen, user, prefill: given, mandatory, onDismiss, onVerified }) => {
  const prefill = given || nationalDigits(user?.phone_cell);
  // Replacing a number that is already verified is a change of second factor, so
  // it takes the password; a first-time verification does not.
  const isChange = !!user?.phone_cell_otp_verified;
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState(prefill);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSend = () => {
    setError("");
    setBusy(true);
    sendPhoneVerificationCode({ phone, password })
      .then((res) => {
        setMaskedPhone(res?.phone_masked || "");
        setCode("");
        setStep("code");
      })
      .catch((err) => setError(messageOf(err, "We could not send the code. Try again.")))
      .finally(() => setBusy(false));
  };

  // The only exit from the wall that is not verifying.
  const handleLogout = () => {
    Logout()
      .catch(() => {})
      .finally(() => {
        window.location = "/";
      });
  };

  const handleVerify = () => {
    setError("");
    setBusy(true);
      verifyPhoneVerificationCode({ code })
        .then((res) => {
          toast.success(res?.message || "Your phone number has been verified.");
          onVerified();
        })
        .catch((err) => {
        setError(messageOf(err, "That code is not correct."));
        setCode("");
      })
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onDismiss}
      dismissible={!mandatory}
      header={isChange ? "Change your phone number" : "Verify your phone number"}
      size="sm"
    >
      <form className={style.body} onSubmit={(e) => e.preventDefault()}>
        {step === "phone" ? (
          <>
            <p className={style.info}>
              {isChange
                ? "Enter your password and the new mobile number. We will text a 6-digit code to it, and your sign-in codes move to that number once it is verified."
                : "Verify the mobile number on your account to continue. Enter your password to confirm it is you — from then on we will text you a 6-digit code each time you sign in."}
            </p>
            {/* Required either way: binding the first number decides where every
                future sign-in code goes, so it takes more than holding the session. */}
            <TextInput
              id="phone_password"
              name="phone_password"
              type="password"
              label="Password"
              placeholder="Enter your password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
            />
            <TextInput
              id="phone_number"
              name="phone_number"
              label="Mobile number"
              prefix={US_DIAL}
              placeholder={US_PHONE_EXAMPLE}
              autoComplete="off"
              value={phone}
              inputMode="numeric"
              maxLength={15}
              onChange={(e) => {
                setPhone(e.target.value.replace(/[^\d\s()+-]/g, ""));
                setError("");
              }}
            />
          </>
        ) : (
          <>
            <p className={style.info}>
              Enter the 6-digit code we texted to{" "}
              <strong>{maskedPhone || "your phone"}</strong>. It expires in 10 minutes.
            </p>
            <TextInput
              id="phone_code"
              name="phone_code"
              label="Verification code"
              placeholder="123456"
              value={code}
              inputMode="numeric"
              maxLength={6}
              autoFocus
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                setError("");
              }}
            />
            <button
              type="button"
              className={style.link}
              onClick={() => {
                setStep("phone");
                setError("");
              }}
            >
              Use a different number
            </button>
          </>
        )}
        {error && <p className={style.error}>{error}</p>}
      </form>
      <div className={style.footer}>
        <Button type="button" variant="ghost" onClick={mandatory ? handleLogout : onDismiss}>
          {mandatory ? "Sign out" : isChange ? "Cancel" : "Remind me later"}
        </Button>
        {step === "phone" ? (
          <Button
            type="button"
            variant="primary"
            onClick={handleSend}
            disabled={busy || !phone || !password}
            isLoading={busy}
          >
            {busy ? "Sending..." : "Send code"}
          </Button>
        ) : (
          <Button type="button" variant="primary" onClick={handleVerify} disabled={busy || code.length !== 6} isLoading={busy}>
            {busy ? "Verifying..." : "Verify"}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default PhoneVerificationModal;
